<template>
  <!--
    The PlatformHeader (EOCube Design System: components/PlatformHeader) with the STAC Browser's own actions in its
    actions slot. Anatomy: brand, product name, primary navigation, the menu button (under bp-nav), spacer, the
    actions (browse, search, favourites, the service, language, theme) and last the session chip or "Sign in".
    It is the `site` row of StacBrowser.vue's sticky header, so the upstream scroll logic still slides it away on
    small screens while the page row stays pinned.
  -->
  <div class="eo eo-header site" id="eocube-header">
    <a class="eo-brand" :href="links.home" :title="$t('eocube.nav.home')">
      <img class="eo-brand-full" :src="logo" alt="EOCube.ro — Powered by ROCS" width="289" height="79">
      <img class="eo-brand-mark" :src="mark" alt="EOCube.ro" width="74" height="80">
    </a>
    <router-link class="eo-product" to="/">{{ $t('eocube.product') }}</router-link>
    <nav :aria-label="$t('eocube.nav.primary')">
      <a v-for="link in navLinks" :key="link.id" :href="link.href">{{ link.label }}</a>
    </nav>
    <button
      ref="navButton" class="eo-hbtn eo-menu-btn" type="button" :aria-label="$t('eocube.nav.menu')"
      aria-haspopup="menu" :aria-expanded="menu === 'nav' ? 'true' : 'false'" @click="toggleMenu('nav')"
    >
      <eo-icon name="menu-2" />
    </button>
    <span class="eo-spacer" />
    <button
      v-if="showBrowse" class="eo-hbtn eo-btn-icon" type="button"
      :title="$t('browse')" :aria-label="$t('browse')" @click="$emit('toggle-sidebar')"
    >
      <eo-icon name="list-tree" />
    </button>
    <router-link
      v-if="canSearch" class="eo-hbtn" :to="searchBrowserLink" :title="$t('search.title')"
      :aria-current="isSearchPage ? 'page' : null"
    >
      <eo-icon name="search" /><span class="eo-hbtn-label">{{ $t('search.title') }}</span>
    </router-link>
    <router-link
      v-if="showFavorites" class="eo-hbtn eocube-favorites" to="/favorites" :title="$t('favorites.title')"
      :aria-current="isFavoritesPage ? 'page' : null"
    >
      <eo-icon name="star" /><span class="eo-hbtn-label">{{ $t('favorites.title') }}</span>
    </router-link>
    <!-- the target of StacBrowser.vue's root popover (the service's facts) -->
    <button v-if="root" id="popover-root-btn" class="eo-hbtn eocube-service" type="button" :title="serviceType">
      <eo-icon name="database" /><span class="eo-hbtn-label">{{ serviceType }}</span>
    </button>
    <LanguageChooser
      v-if="supportedLocales.length > 1" :data="data" :current-locale="locale" :locales="supportedLocales"
      @set-locale="l => switchLocale({locale: l, userSelected: true})"
    />
    <button
      v-if="canToggleTheme" class="eo-hbtn eo-btn-icon" type="button" :aria-pressed="dark ? 'true' : 'false'"
      :title="$t(dark ? 'eocube.theme.light' : 'eocube.theme.dark')" :aria-label="$t('eocube.theme.dark')"
      @click="$emit('toggle-color-mode')"
    >
      <eo-icon :name="dark ? 'sun' : 'moon'" />
    </button>
    <template v-if="canAuthenticate">
      <eo-session
        v-if="isLoggedIn" ref="session" :name="session.name" :expires="session.expires || null"
        @eo-session-toggle="onSessionToggle"
      />
      <button
        v-else class="eo-hbtn eo-hbtn-accent" type="button" :aria-label="$t('eocube.session.signIn')"
        @click="$emit('log-in-out')"
      >
        <eo-icon name="user" /><span>{{ $t('eocube.session.signIn') }}</span>
      </button>
    </template>
  </div>
  <!-- The header's drop-downs live on <body> (fixed), so nothing in the header clips them. -->
  <Teleport to="body">
    <div
      v-if="menu" ref="menuNode" class="eo eo-session-menu eocube-menu" role="menu"
      :style="menuPosition" :aria-label="menu === 'nav' ? $t('eocube.nav.primary') : $t('eocube.session.account')"
      @keydown="onMenuKeydown" @click="onMenuClick"
    >
      <template v-if="menu === 'nav'">
        <a v-for="link in navLinks" :key="link.id" role="menuitem" :href="link.href">
          <eo-icon :name="link.icon" /> {{ link.label }}
        </a>
      </template>
      <template v-else>
        <!-- who is signed in: the name, and the username when the chip shows a full name; never the realm -->
        <div class="eo-who">
          <b>{{ session.name }}</b>
          <span v-if="session.username && session.username !== session.name">{{ session.username }}</span>
        </div>
        <a v-if="accountUrl" role="menuitem" :href="accountUrl" target="_blank" rel="noopener noreferrer">
          <eo-icon name="user" /> {{ $t('eocube.session.profile') }}
        </a>
        <button role="menuitem" type="button" @click="$emit('log-in-out')">
          <eo-icon name="logout" /> {{ $t('eocube.session.signOut') }}
        </button>
      </template>
    </div>
  </Teleport>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import logo from './vendor/logos/Logo-EOCubeRO-white.svg';
import mark from './vendor/logos/Logo-EOCubeRO-mark-white.svg';
import { PLATFORM, claimsOf } from './platform';

export default {
  name: 'EocubeHeader',
  components: {
    LanguageChooser: defineAsyncComponent(() => import('../components/LanguageChooser.vue'))
  },
  props: {
    colorMode: {
      type: String,
      default: null
    },
    canToggleTheme: {
      type: Boolean,
      default: true
    }
  },
  emits: ['toggle-sidebar', 'toggle-color-mode', 'log-in-out'],
  data() {
    return {
      logo,
      mark,
      menu: null,
      menuPosition: {}
    };
  },
  computed: {
    ...mapState(['authConfig', 'conformsTo', 'data', 'locale', 'supportedLocales', 'showFavorites']),
    ...mapState('auth', ['credentials']),
    ...mapGetters(['canSearch', 'root', 'searchBrowserLink']),
    ...mapGetters('auth', ['canAuthenticate', 'isLoggedIn']),
    ...mapGetters('auth', { authMethod: 'method' }),
    dark() {
      return this.colorMode === 'dark';
    },
    links() {
      return PLATFORM.links(this.locale);
    },
    navLinks() {
      return [
        { id: 'home', href: this.links.home, label: this.$t('eocube.nav.home'), icon: 'home' },
        { id: 'notebooks', href: this.links.notebooks, label: this.$t('eocube.nav.notebooks'), icon: 'notebook' },
        { id: 'docs', href: this.links.docs, label: this.$t('eocube.nav.docs'), icon: 'book-2' }
      ];
    },
    showBrowse() {
      return this.$route.name !== 'select';
    },
    isSearchPage() {
      return this.$route.name === 'search';
    },
    isFavoritesPage() {
      return this.$route.name === 'favorites';
    },
    serviceType() {
      const isApi = Array.isArray(this.conformsTo) && this.conformsTo.length > 0;
      return isApi ? this.$t('index.api') : this.$t('index.catalog');
    },
    // Who is signed in, from the OIDC user (oidc-client-ts) or else the access token's claims. Depends on the
    // credentials so that it follows every (silent) renewal: the chip's countdown restarts with the new token.
    session() {
      if (!this.credentials) {
        return {};
      }
      const user = this.authMethod && this.authMethod.user;
      const claims = Object.assign({}, claimsOf(this.credentials), user && user.profile);
      const username = claims.preferred_username || null;
      return {
        name: claims.name || username || claims.email || this.$t('eocube.session.signedIn'),
        username,
        expires: (user && user.expires_at) || claims.exp || null
      };
    },
    accountUrl() {
      return PLATFORM.accountUrl(this.authConfig);
    }
  },
  watch: {
    $route() {
      this.closeMenu();
    },
    isLoggedIn() {
      this.closeMenu();
    }
  },
  beforeUnmount() {
    this.closeMenu();
  },
  methods: {
    ...mapActions(['switchLocale']),
    toggleMenu(which) {
      if (this.menu === which) {
        this.closeMenu(true);
      }
      else {
        this.openMenu(which);
      }
    },
    onSessionToggle(event) {
      if (event.detail && event.detail.open) {
        this.openMenu('session');
      }
      else {
        this.closeMenu();
      }
    },
    anchorOf(which) {
      if (which === 'nav') {
        return this.$refs.navButton;
      }
      return this.$refs.session && this.$refs.session.querySelector('button');
    },
    // A menu hangs under its anchor, aligned to the anchor's nearer viewport edge.
    async openMenu(which) {
      this.closeMenu();
      const anchor = this.anchorOf(which);
      if (!anchor) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const position = { top: `${Math.round(rect.bottom + 4)}px` };
      if (rect.left + rect.width / 2 < window.innerWidth / 2) {
        position.left = `${Math.max(8, Math.round(rect.left))}px`;
      }
      else {
        position.right = `${Math.max(8, Math.round(window.innerWidth - rect.right))}px`;
      }
      this.menuPosition = position;
      this.menu = which;
      document.addEventListener('pointerdown', this.onPointerDown, true);
      window.addEventListener('resize', this.dismiss);
      window.addEventListener('scroll', this.dismiss, true);
      await this.$nextTick();
      const first = this.$refs.menuNode && this.$refs.menuNode.querySelector('[role="menuitem"]');
      if (first) {
        first.focus();
      }
    },
    closeMenu(restoreFocus = false) {
      if (!this.menu) {
        return;
      }
      const anchor = this.anchorOf(this.menu);
      this.menu = null;
      document.removeEventListener('pointerdown', this.onPointerDown, true);
      window.removeEventListener('resize', this.dismiss);
      window.removeEventListener('scroll', this.dismiss, true);
      if (anchor) {
        // the chip toggles its own aria-expanded on click; keep it right when the menu closes otherwise
        anchor.setAttribute('aria-expanded', 'false');
        if (restoreFocus) {
          anchor.focus();
        }
      }
    },
    dismiss() {
      this.closeMenu();
    },
    onPointerDown(event) {
      const anchor = this.anchorOf(this.menu);
      const node = this.$refs.menuNode;
      if (node && !node.contains(event.target) && !(anchor && anchor.contains(event.target))) {
        this.closeMenu();
      }
    },
    onMenuClick(event) {
      if (event.target.closest('[role="menuitem"]')) {
        this.closeMenu();
      }
    },
    onMenuKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMenu(true);
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }
      const items = Array.from(this.$refs.menuNode.querySelectorAll('[role="menuitem"]'));
      const i = items.indexOf(document.activeElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(Math.max(i, step > 0 ? -1 : 0) + step + items.length) % items.length].focus();
      event.preventDefault();
    }
  }
};
</script>
